// Moment-level payload renderers shared by every trace surface — the X-ray
// lens bodies (XRayBody), the trace-detail work feed (TraceWorkFeed), and the
// Lanes dock (TraceLanesView) all render "what one op showed the agent"
// through these. Extracted from XRayBody so the feed no longer imports its
// renderers FROM a lens body (and so a lens body could never need the feed —
// that edge is a cycle). Mechanical move: bodies are unchanged.

import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import ScrollArea from "./ScrollArea.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { formatCompact } from "../lib/renderers.js";
import { diffLines, reconstructFile, classifyLine } from "../lib/traceFileTree.js";
import { resultObsDoc } from "../lib/traceQueryOps.js";
import { canBlameLine } from "../lib/traceLineBlame.js";
import { SurfaceChart } from "./ChartBlocks.jsx";
import { Component as KernelTable } from "./kernelPrimitives/table.jsx";
import { Component as KernelBars } from "./kernelPrimitives/bars.jsx";
import { Component as KernelChart } from "./kernelPrimitives/chart.jsx";

export const SAMPLE_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
});

export const sampleWhen = (ts) => {
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? "–" : SAMPLE_TIME_FMT.format(d);
};

// Sized to the scrubber bar's 24px control height — the chip sits on the
// same row as the Sample select and the Prompts toggle.
export function FilterChip({ label, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex h-[24px] shrink-0 items-center rounded-[2px] border px-[8px] text-[10px] tracking-[0.04em] uppercase transition-colors cursor-pointer",
        active
          ? "border-[color:var(--green)] bg-[color:var(--green-soft)] text-[color:var(--ink)]"
          : "border-[color:var(--line)] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

export const PLUS_STYLE = { color: "var(--green)" };
export const MINUS_STYLE = { color: "var(--red,#c0392b)" };

// PR-style diff row tints — color-mix alpha, hard rows, no gradients.
export const DIFF_BG = {
  add: "color-mix(in srgb, var(--green) 10%, transparent)",
  del: "color-mix(in srgb, var(--red,#c0392b) 10%, transparent)",
};
const DIFF_ROW_LIMIT = 40;

// Unanchored hunks rendered as a plain PR-style diff — the only changes
// still shown as detached blocks; everything that CAN be anchored is
// annotated inline on the reconstruction itself (AnnotatedFileBody).
// A bordered mono chip carrying a glyph in a category color — the single
// gutter-marker style, shared by the provenance chips and the diff markers.
export function markerChip(sym, color) {
  return (
    <span className={CHIP_CLASS} style={{ color, borderColor: `color-mix(in srgb, ${color} 45%, var(--line))` }}>
      {sym}
    </span>
  );
}

// Provenance card for a bare op reference (no version history) — used by
// the unanchored-edit gutter, where a hunk couldn't be placed into the
// reconstruction but still came from exactly one trace.
export function TraceRefCard({ op, traceOf, onSelectTrace, heading }) {
  const m = timeAndSummary(op, traceOf);
  return (
    <div className="grid min-w-0">
      <span className="border-b border-[color:var(--line-soft)] px-[10px] py-[6px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
        {heading}
      </span>
      {op ? (
        <button
          type="button"
          onClick={() => onSelectTrace(op.promptId)}
          className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-baseline gap-[8px] px-[10px] py-[4px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
        >
          <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">{m.when}</span>
          <span className="truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">{m.summary}</span>
        </button>
      ) : null}
      <span className="pb-[4px]" />
    </div>
  );
}

// The ONE gutter row used everywhere — reconstruction lines, tombstones,
// and unanchored diffs. Layout is constant: [line-number 42px][chip 26px]
// [text], and the gutter (number + chip together) is a single hover target
// that surfaces the line's provenance card — GitHub-blame style, so every
// line is hoverable and every section's text aligns to the same column.
// The gutter is a STICKY left column (opaque bg + right border, IDE-style):
// line numbers and provenance chips stay pinned while the code text scrolls
// horizontally under them. The row tint rides the text span only, so the
// gutter reads as neutral chrome; the whole gutter is the hover target.
const GUTTER_CLS =
  "sticky left-0 z-[1] flex shrink-0 items-center self-stretch bg-[color:var(--bg)] border-r border-[color:var(--line-soft)]";

export function GutterLine({ no, chip, card, tint, text, textClass = "text-[color:var(--ink)]" }) {
  const gutter = (
    <>
      <span className="w-[42px] shrink-0 select-none pr-[8px] text-right text-[10px] tabular-nums text-[color:var(--muted-soft)]">{no ?? ""}</span>
      <span className="w-[26px] shrink-0 text-center">{chip}</span>
    </>
  );
  return (
    <div className="flex items-stretch font-mono text-[11px] leading-[1.6]">
      {card ? (
        <HoverCard placement="side" className={`${GUTTER_CLS} cursor-default hover:bg-[color:var(--control-bg)]`} card={card}>
          {gutter}
        </HoverCard>
      ) : (
        <span className={GUTTER_CLS}>{gutter}</span>
      )}
      <span
        className={`flex-1 whitespace-pre py-[1px] pl-[10px] pr-[12px] ${textClass}`}
        style={tint ? { backgroundColor: tint } : undefined}
      >
        {text === "" ? " " : text}
      </span>
    </div>
  );
}

// Unanchored hunks (targets never observed, so unplaceable in the
// reconstruction) rendered with the SAME gutter as everything else: no
// line number, a +/−/· marker chip, and each row hoverable to the one
// trace whose edit proposed it.
export function DiffBlock({ hunks, op, traceOf, onSelectTrace }) {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => {
    const out = [];
    (hunks || []).forEach((h, i) => {
      if (i) out.push({ t: "hunk" });
      out.push(...diffLines(h.old, h.new));
    });
    return out;
  }, [hunks]);
  const visible = showAll ? rows : rows.slice(0, DIFF_ROW_LIMIT);
  const hidden = rows.length - visible.length;
  const card = op ? (
    <TraceRefCard op={op} traceOf={traceOf} onSelectTrace={onSelectTrace} heading="Unanchored edit from this trace" />
  ) : null;
  const chipFor = (t) =>
    t === "add" ? markerChip("+", "var(--green)")
      : t === "del" ? markerChip("−", "var(--red,#c0392b)")
        : markerChip("·", "var(--muted)");
  return (
    <div className="min-w-0 max-w-full">
      <div className="overflow-x-auto">
        <div className="min-w-max">
          {visible.map((r, i) =>
            r.t === "hunk" ? (
              <div key={i} className="bg-[color:var(--line-soft)] pl-[68px] pr-[10px] py-[1px] font-mono text-[10px] leading-[1.6] text-[color:var(--muted-soft)]">
                @@
              </div>
            ) : (
              <GutterLine
                key={i}
                no={null}
                chip={chipFor(r.t)}
                card={card}
                tint={r.t === "ctx" ? null : DIFF_BG[r.t]}
                text={r.text}
                textClass={r.t === "ctx" ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}
              />
            ),
          )}
        </div>
      </div>
      {hidden > 0 || (showAll && rows.length > DIFF_ROW_LIMIT) ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="block w-full border-t border-[color:var(--line-soft)] px-[10px] py-[3px] text-left font-mono text-[10px] text-[color:var(--muted)] cursor-pointer hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)]"
        >
          {hidden > 0 ? `⌄ show ${hidden} more line${hidden === 1 ? "" : "s"}` : "⌃ collapse"}
        </button>
      ) : null}
    </div>
  );
}

// The FLUSH diff treatment for narrow payload surfaces (the trace work feed,
// the Lanes dock): blind hunks as authored — no gutter, no per-line history
// (the frame's basis strip carries the provenance there) — with wrapping text
// instead of DiffBlock's horizontally-scrolling gutter rows. Takes the
// reconstruction's `blind` list directly ({ hunks } entries).
const DIFF_ROW_CAP = 30;

export function DiffRows({ blind, cap = DIFF_ROW_CAP }) {
  const [showAll, setShowAll] = useState(false);
  const rows = useMemo(() => {
    const out = [];
    for (const b of blind || []) {
      for (const h of b.hunks || []) {
        if (out.length) out.push({ t: "hunk" });
        out.push(...diffLines(h.old, h.new));
      }
    }
    return out;
  }, [blind]);
  if (!rows.length) return null;
  const visible = showAll ? rows : rows.slice(0, cap);
  const hidden = rows.length - visible.length;
  return (
    <div className="min-w-0 py-[2px]">
      {visible.map((r, i) =>
        r.t === "hunk" ? (
          <div key={i} className="px-[7px] py-[1px] font-mono text-[11px] leading-[1.5] text-[color:var(--muted-soft)]">
            @@
          </div>
        ) : (
          <div
            key={i}
            className="flex min-w-0 items-stretch px-[7px] font-mono text-[12px] leading-[1.5]"
            style={r.t === "ctx" ? undefined : { backgroundColor: DIFF_BG[r.t] }}
          >
            <span className={`w-[12px] shrink-0 select-none ${r.t === "add" ? "text-[color:var(--green)]" : r.t === "del" ? "text-[color:var(--red,#c0392b)]" : "text-[color:var(--muted-soft)]"}`}>
              {r.t === "add" ? "+" : r.t === "del" ? "−" : " "}
            </span>
            <span className={`min-w-0 flex-1 whitespace-pre-wrap break-words ${r.t === "ctx" ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>
              {r.text === "" ? " " : r.text}
            </span>
          </div>
        ),
      )}
      {hidden > 0 || (showAll && rows.length > cap) ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="block px-[7px] py-[2px] text-left font-mono text-[11px] text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)]"
        >
          {hidden > 0 ? `+${hidden} more rows` : "collapse"}
        </button>
      ) : null}
    </div>
  );
}

// Interactive hover popover — unlike HoverTooltip (pointer-events-none),
// the card must host CLICKABLE content (trace rows, the line-history
// stepper), so it manages its own hover-linger between trigger and card
// and dismisses on outside scroll or mouse-leave — NOT on click, so
// in-card controls work.
const HOVER_CARD_W = 360;

export function HoverCard({ card, children, className = "min-w-0 flex-1", placement = "below" }) {
  const [pos, setPos] = useState(null);
  const popRef = useRef(null);
  const correctedRef = useRef(false);
  const closeTimer = useRef(null);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setPos(null), 140); };
  const openAt = (e) => {
    cancelClose();
    const r = e.currentTarget.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (placement === "side") {
      // Beside the trigger, preferring LEFT (so the card clears the code
      // column's vertical scroll path); flip right only if the left gutter
      // has no room. Top-anchored to the row, clamped on-screen.
      const top = Math.max(8, Math.min(r.top - 2, vh - 8));
      const leftOfTrigger = r.left - 8 - HOVER_CARD_W;
      setPos(leftOfTrigger >= 8
        ? { top, left: leftOfTrigger }
        : { top, left: Math.min(r.right + 8, vw - HOVER_CARD_W - 8) });
    } else {
      setPos({ top: Math.min(r.bottom + 4, vh - 80), left: Math.max(8, Math.min(r.left, vw - HOVER_CARD_W - 8)) });
    }
  };
  useEffect(() => () => cancelClose(), []);
  useEffect(() => {
    if (!pos) return undefined;
    const onScroll = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      setPos(null);
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [pos]);
  // Once mounted, if the card runs off the viewport bottom, lift it so it
  // stays fully visible (height is already capped by max-h + internal
  // scroll). Runs once per open.
  useLayoutEffect(() => {
    if (!pos || correctedRef.current) return;
    const el = popRef.current;
    if (!el) return;
    correctedRef.current = true;
    const b = el.getBoundingClientRect();
    const vh = window.innerHeight;
    const M = 8;
    if (b.bottom > vh - M) {
      setPos((p) => ({ ...p, top: Math.max(M, vh - M - b.height) }));
    }
  }, [pos]);
  useEffect(() => { if (!pos) correctedRef.current = false; }, [pos]);
  return (
    <>
      <div className={className} onMouseEnter={openAt} onMouseLeave={scheduleClose}>
        {children}
      </div>
      {pos ? createPortal(
        <div
          ref={popRef}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
          className="fixed z-[9999] max-h-[46vh] w-[360px] overflow-y-auto border border-[color:var(--line-strong)] bg-[color:var(--bg)] shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          {card}
        </div>,
        document.body,
      ) : null}
    </>
  );
}

export const CHIP_CLASS = "inline-grid h-[14px] min-w-[16px] place-items-center rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[3px] font-mono text-[9px] leading-none cursor-default";

const DRIFT_COLOR = "var(--amber,#c8922e)";

// Per-provenance-kind gutter treatment (classifyLine → visuals). glyph is
// the fallback chip when there's a single version; a multi-version line
// shows its version count instead. `tint` backs the whole row.
export const PROV = {
  written:  { glyph: "+", color: "var(--green)", tint: DIFF_BG.add, label: "written in-window" },
  inserted: { glyph: "+", color: "var(--green)", tint: DIFF_BG.add, label: "inserted by an edit" },
  edited:   { glyph: "±", color: "var(--green)", tint: DIFF_BG.add, label: "modified by an edit" },
  drift:    { glyph: "≠", color: DRIFT_COLOR, tint: `color-mix(in srgb, ${DRIFT_COLOR} 13%, transparent)`, label: "changed off-tool (drift)" },
  context:  { glyph: "·", color: "var(--muted)", tint: null, label: "edit's unchanged context" },
  read:     { glyph: "", color: "var(--muted-soft)", tint: null, label: "read only" },
};

// The gutter chip: version count for a multi-version line (colored by its
// provenance), else the kind glyph. Its border picks up the kind color so
// the category reads even before hovering.
export function GutterChip({ entry }) {
  const kind = classifyLine(entry);
  const p = PROV[kind];
  const text = entry.versions.length > 1 ? String(entry.versions.length) : (p.glyph || "·");
  return (
    <span
      className={CHIP_CLASS}
      style={{ color: p.color, borderColor: `color-mix(in srgb, ${p.color} 45%, var(--line))` }}
    >
      {text}
    </span>
  );
}

// One-line key for the gutter markers — rendered above the annotated body
// so the glyphs/colors are decodable at a glance.
export function ProvenanceLegend() {
  const items = ["written", "edited", "drift", "context"];
  return (
    <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[4px]">
      {items.map((k) => (
        <span key={k} className="flex items-center gap-[5px] text-[10px] leading-none text-[color:var(--muted)]">
          <span className={CHIP_CLASS} style={{ color: PROV[k].color, borderColor: `color-mix(in srgb, ${PROV[k].color} 45%, var(--line))` }}>
            {PROV[k].glyph}
          </span>
          {PROV[k].label}
        </span>
      ))}
      <span className="text-[10px] leading-none text-[color:var(--muted-soft)]">number = versions · − = deleted line</span>
    </div>
  );
}

export const timeAndSummary = (op, traceOf) => {
  const row = op ? traceOf.get(op.promptId) : null;
  return { when: sampleWhen(row?.timestamp ?? op?.ts), summary: row?.summary || op?.promptId || "" };
};

// The per-line history card: step back and forth through every version
// the window saw of this line, each tied to the trace that produced it,
// plus the full list of ops that touched the line.
export function LineHistoryCard({ no, entry, traceOf, onSelectTrace, filePath, onBlameLine }) {
  const [idx, setIdx] = useState(entry.versions.length - 1);
  const v = entry.versions[Math.min(idx, entry.versions.length - 1)];
  const meta = v?.op ? timeAndSummary(v.op, traceOf) : null;
  const kind = classifyLine(entry);
  return (
    <div className="grid min-w-0">
      <div className="border-b border-[color:var(--line-soft)] px-[10px] py-[6px]">
        <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
          Line {no} · {entry.versions.length} version{entry.versions.length === 1 ? "" : "s"} · {entry.touches.length} touch{entry.touches.length === 1 ? "" : "es"}
        </span>
        <span className="mt-[2px] flex items-center gap-[5px] text-[10px] text-[color:var(--muted)]">
          <span className={CHIP_CLASS} style={{ color: PROV[kind].color, borderColor: `color-mix(in srgb, ${PROV[kind].color} 45%, var(--line))` }}>
            {PROV[kind].glyph || "·"}
          </span>
          {PROV[kind].label}
        </span>
        {entry.trimmed ? (
          <span className="mt-[2px] block text-[10px] text-[color:var(--muted)]">Seen trimmed: whitespace at its start or end was not printed.</span>
        ) : null}
      </div>
      <div className="flex items-center gap-[8px] border-b border-[color:var(--line-soft)] px-[10px] py-[5px]">
        <button
          type="button"
          disabled={idx <= 0}
          onClick={() => setIdx((i) => Math.max(0, i - 1))}
          className="grid h-[20px] w-[20px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[12px] leading-none text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-40"
          aria-label="Earlier version"
        >
          ‹
        </button>
        <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
          v{Math.min(idx, entry.versions.length - 1) + 1}/{entry.versions.length}
        </span>
        <button
          type="button"
          disabled={idx >= entry.versions.length - 1}
          onClick={() => setIdx((i) => Math.min(entry.versions.length - 1, i + 1))}
          className="grid h-[20px] w-[20px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[12px] leading-none text-[color:var(--muted)] cursor-pointer hover:text-[color:var(--ink)] disabled:cursor-default disabled:opacity-40"
          aria-label="Later version"
        >
          ›
        </button>
        {v?.op ? (
          <span className="min-w-0 truncate text-[10px] text-[color:var(--muted)]">
            {meta.when} · {v.op.kind}
          </span>
        ) : null}
      </div>
      <pre className="m-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[10px] py-[6px] font-mono text-[11px] leading-[1.5] text-[color:var(--ink)]">
        {v?.text === "" ? " " : v?.text}
      </pre>
      {v?.op ? (
        <button
          type="button"
          onClick={() => onSelectTrace(v.op.promptId)}
          className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-baseline gap-[8px] border-b border-[color:var(--line-soft)] px-[10px] py-[4px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
        >
          <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">{meta.when}</span>
          <span className="truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">{meta.summary}</span>
        </button>
      ) : null}
      <span className="px-[10px] pt-[5px] pb-[2px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Touched by</span>
      {entry.touches.slice(0, 6).map((tOp, i) => {
        const m = timeAndSummary(tOp, traceOf);
        return (
          <button
            key={i}
            type="button"
            onClick={() => onSelectTrace(tOp.promptId)}
            className="grid w-full grid-cols-[auto_auto_minmax(0,1fr)] items-baseline gap-[8px] px-[10px] py-[3px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
          >
            <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">{m.when}</span>
            <span
              className="font-mono text-[10px] leading-[1.3]"
              style={tOp.kind === "read" ? { color: "var(--muted-soft)" } : PLUS_STYLE}
            >
              {tOp.kind}
            </span>
            <span className="truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">{m.summary}</span>
          </button>
        );
      })}
      {entry.touches.length > 6 ? (
        <span className={`${NOTE_CLASS} px-[10px] py-[3px]`}>+{entry.touches.length - 6} more</span>
      ) : null}
      {onBlameLine && canBlameLine(entry, filePath) ? (
        <button
          type="button"
          onClick={() => onBlameLine(entry.text, filePath)}
          className="border-t border-[color:var(--line-soft)] px-[10px] py-[5px] text-left text-[11px] text-[color:var(--green)] cursor-pointer hover:bg-[color:var(--control-bg)]"
        >
          Blame this line across all requests →
        </button>
      ) : null}
      <span className="pb-[4px]" />
    </div>
  );
}

// The card for a deleted line (tombstone): the removed text and the trace
// whose edit removed it.
export function TombCard({ text, op, traceOf, onSelectTrace }) {
  const m = timeAndSummary(op, traceOf);
  return (
    <div className="grid min-w-0">
      <span className="border-b border-[color:var(--line-soft)] px-[10px] py-[6px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
        Deleted line
      </span>
      <pre className="m-0 max-w-full whitespace-pre-wrap [overflow-wrap:anywhere] border-b border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[10px] py-[6px] font-mono text-[11px] leading-[1.5]" style={MINUS_STYLE}>
        {text === "" ? " " : text}
      </pre>
      {op ? (
        <button
          type="button"
          onClick={() => onSelectTrace(op.promptId)}
          className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-baseline gap-[8px] px-[10px] py-[4px] text-left cursor-pointer hover:bg-[color:var(--control-bg)]"
        >
          <span className="text-[10px] tabular-nums leading-[1.3] text-[color:var(--muted)]">{m.when}</span>
          <span className="truncate text-[11px] leading-[1.3] text-[color:var(--ink)]">{m.summary}</span>
        </button>
      ) : null}
      <span className="pb-[4px]" />
    </div>
  );
}

// The annotated reconstruction: the file's replayed content with per-line
// provenance — gutter chips open the line-history card, added/modified
// lines tinted, deletions rendered in place as tombstone rows, unobserved
// regions as explicit gap bands.
// Exported for the trace-detail X-ray feed (TraceWorkFeed), which renders a
// single trace's per-file op stack with this same gutter/provenance rail.
export function AnnotatedFileBody({ rec, traceOf, onSelectTrace, cap = 0, bordered = false, filePath, onBlameLine }) {
  const [showAll, setShowAll] = useState(false);
  const rows = rec.rows;
  const visible = cap > 0 && !showAll ? rows.slice(0, cap) : rows;
  const hidden = rows.length - visible.length;
  if (!rows.length) {
    return <span className={`${NOTE_CLASS} block px-[10px] py-[6px]`}>No content observed in this window.</span>;
  }
  // Flush by default (renders inside the file section's own frame); the
  // standalone detail panel opts into a border with `bordered`.
  const wrapCls = bordered
    ? "min-w-0 max-w-full rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)]"
    : "min-w-0 max-w-full";
  return (
    <div className={wrapCls}>
      <div className="overflow-x-auto">
        <div className="min-w-max py-[2px]">
          {visible.map((r, i) => {
            if (r.gap) {
              return (
                <div key={i} className="bg-[color:var(--line-soft)] pl-[68px] pr-[10px] py-[1px] font-mono text-[10px] leading-[1.7] text-[color:var(--muted-soft)]">
                  {r.gap[0] === r.gap[1] ? `… line ${r.gap[0]} not observed` : `… lines ${r.gap[0]}–${r.gap[1]} not observed`}
                </div>
              );
            }
            if (r.del) {
              return (
                <GutterLine
                  key={i}
                  no={null}
                  chip={markerChip("−", "var(--red,#c0392b)")}
                  card={<TombCard text={r.text} op={r.op} traceOf={traceOf} onSelectTrace={onSelectTrace} />}
                  tint={DIFF_BG.del}
                  text={r.text}
                  textClass="text-[color:var(--muted)]"
                />
              );
            }
            const kind = classifyLine(r.entry);
            return (
              <GutterLine
                key={i}
                no={r.no}
                chip={<GutterChip entry={r.entry} />}
                card={<LineHistoryCard no={r.no} entry={r.entry} traceOf={traceOf} onSelectTrace={onSelectTrace} filePath={filePath} onBlameLine={onBlameLine} />}
                tint={PROV[kind].tint}
                text={r.text}
                {...(r.entry.trimmed ? { textClass: "text-[color:var(--muted)]" } : {})}
              />
            );
          })}
        </div>
      </div>
      {cap > 0 && (hidden > 0 || (showAll && rows.length > cap)) ? (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="block w-full border-t border-[color:var(--line-soft)] px-[10px] py-[3px] text-left font-mono text-[10px] text-[color:var(--muted)] cursor-pointer hover:bg-[color:var(--control-bg)] hover:text-[color:var(--ink)]"
        >
          {hidden > 0 ? `⌄ show ${hidden} more row${hidden === 1 ? "" : "s"}` : "⌃ collapse"}
        </button>
      ) : null}
    </div>
  );
}

// Rich rendering of a Grafana response — series become sparklines (range) or
// value bars (instant) on the house chart primitives; log entries become
// level-chipped rows; the empty case shows Grafana's own hint. Falls back to
// the annotated text document when the payload doesn't parse.
export function GrafanaResultView({ view, traceOf, run, onSelectTrace }) {
  if (view.type === "hint") {
    return <span className={`${NOTE_CLASS} block px-[2px]`}>{view.summary}</span>;
  }
  if (view.type === "rules") {
    return (
      <div className="grid gap-[2px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[5px]">
        {view.rules.map((r, i) => (
          <div key={i} className="flex min-w-0 items-baseline gap-[8px]">
            <span
              className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.04em]"
              style={/firing|alerting|error/i.test(r.state || "") ? MINUS_STYLE : { color: "var(--muted-soft)" }}
            >
              {r.state || "?"}
            </span>
            <span className="min-w-0 truncate font-mono text-[10.5px] leading-[1.6] text-[color:var(--ink)]">{r.title}</span>
            {r.group ? <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">{r.group}</span> : null}
            {r.health && r.health !== "ok" ? <span className="shrink-0 font-mono text-[10px]" style={MINUS_STYLE}>{r.health}</span> : null}
          </div>
        ))}
        {view.total > view.rules.length ? (
          <span className={NOTE_CLASS}>… {view.total - view.rules.length} more rules</span>
        ) : null}
      </div>
    );
  }
  if (view.type === "logs") {
    return (
      <div className="grid gap-[2px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[5px]">
        {view.entries.map((e, i) => (
          <div key={i} className="flex min-w-0 items-baseline gap-[8px]">
            <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">
              {e.ts ? sampleWhen(e.ts) : "–"}
            </span>
            {e.level ? (
              <span
                className="shrink-0 font-mono text-[9.5px] uppercase tracking-[0.04em]"
                style={/error|fatal/i.test(e.level) ? MINUS_STYLE : /warn/i.test(e.level) ? { color: "var(--plot-ink)" } : { color: "var(--muted-soft)" }}
              >
                {e.level}
              </span>
            ) : null}
            <span className="min-w-0 truncate font-mono text-[10.5px] leading-[1.6] text-[color:var(--muted)]">{e.message}</span>
          </div>
        ))}
        {view.total > view.entries.length ? (
          <span className={NOTE_CLASS}>… {view.total - view.entries.length} more entries</span>
        ) : null}
      </div>
    );
  }
  // Series render on the kernel primitives: instant sets → the bars
  // primitive; a single range series → the chart primitive (line); multiple
  // range series → per-series sparkline rows (the chart primitive's layer
  // mode stacks, which would misrepresent independent series).
  const seriesLabel = (sr, i) => {
    const chips = view.varying.map((k) => sr.labels?.[k]).filter(Boolean);
    return chips.length ? chips.join(" · ") : sr.name || `series ${i + 1}`;
  };
  return (
    <div className="grid gap-[3px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px]">
      {view.instant ? (
        <KernelBars
          spec={{ labelField: "label", valueField: "value", maxItems: 10, format: "number" }}
          data={view.series.map((sr, i) => ({ label: seriesLabel(sr, i), value: sr.last }))}
        />
      ) : view.series.length === 1 ? (
        <KernelChart
          spec={{ height: 110, mode: "line", title: seriesLabel(view.series[0], 0) }}
          data={view.series[0].points}
          fillHeight={false}
        />
      ) : (
        view.series.map((sr, i) => (
          <div key={i} className="flex min-w-0 items-center gap-[10px]">
            <span className="min-w-0 flex-1 truncate font-mono text-[10.5px] leading-[1.6] text-[color:var(--muted)]">{seriesLabel(sr, i)}</span>
            <div className="w-[160px] shrink-0">
              <SurfaceChart
                mode="line"
                series={sr.points}
                stroke="var(--green)"
                className="relative min-h-[16px] text-[color:var(--plot-ink-soft)]"
                padding={2}
                width={160}
                height={16}
                strokeWidth={1.4}
                haloWidth={3.8}
                haloStroke="var(--bg)"
                fillOpacity={0.25}
              />
            </div>
            <span className="w-[110px] shrink-0 text-right font-mono text-[10px] tabular-nums text-[color:var(--muted)]">
              <span className="text-[color:var(--ink)]">{formatCompact(sr.last)}</span> · max {formatCompact(sr.max)}
            </span>
          </div>
        ))
      )}
      {view.total > view.series.length ? (
        <span className={NOTE_CLASS}>… {view.total - view.series.length} more series</span>
      ) : null}
      {run ? (
        <button
          type="button"
          onClick={() => onSelectTrace(run.promptId)}
          className="justify-self-end text-[10px] text-[color:var(--muted-soft)] cursor-pointer hover:text-[color:var(--ink)]"
        >
          open trace →
        </button>
      ) : null}
    </div>
  );
}

// One observed response: rich by default (the kernel table primitive for
// tabular payloads — its own CSV download included — or the telemetry view),
// with a Raw toggle back to the exact text the agent's context received.
//
// Exported for the trace-detail X-ray feed (TraceWorkFeed), which renders the
// same responses per-op inside one trace. `run` needs { result, table?, view?,
// promptId, ts }; the feed keys its traceOf map by turn index.
export function ResponseBlock({ run, engine, traceOf, onSelectTrace, resCap }) {
  const [showRaw, setShowRaw] = useState(false);
  const rich = useMemo(() => {
    if (run.table && run.table.columns?.length) return { kind: "table", table: run.table };
    if (run.view && (engine === "prometheus" || engine === "loki" || engine === "alerts")) return { kind: "grafana", view: run.view };
    return null;
  }, [run, engine]);
  const rawRec = useMemo(
    () => ((showRaw || !rich) && run.result?.text ? reconstructFile([resultObsDoc(run)]) : null),
    [showRaw, rich, run],
  );
  const tableData = useMemo(() => {
    if (rich?.kind !== "table") return null;
    const t = rich.table;
    return {
      spec: { columns: t.columns.map((c, i) => ({ id: `c${i}`, label: c })), maxRows: 12 },
      data: t.rows.map((row) => Object.fromEntries(row.map((v, i) => [`c${i}`, v]))),
    };
  }, [rich]);
  return (
    <div className="grid gap-[3px]">
      <span className="flex min-w-0 items-baseline gap-[8px]">
        <span className={`${NOTE_CLASS} min-w-0 truncate`}>
          response observed · {sampleWhen(traceOf.get(run.promptId)?.timestamp ?? run.ts)}
          {run.result?.shared
            ? ` · ${run.result.shared} statements shared the command output, so it cannot be split by query`
            : ""}
        </span>
        {rich ? (
          <span className="ml-auto flex shrink-0 items-baseline gap-[4px]">
            <FilterChip label="Rich" active={!showRaw} onClick={() => setShowRaw(false)} />
            <FilterChip label="Raw" active={showRaw} onClick={() => setShowRaw(true)} />
          </span>
        ) : null}
      </span>
      {!showRaw && rich ? (
        rich.kind === "table" ? (
          <div className="grid gap-[2px] rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--plot-bg)] px-[8px] py-[6px]">
            <KernelTable spec={tableData.spec} data={tableData.data} />
            {rich.table.total > rich.table.rows.length ? (
              <span className={NOTE_CLASS}>… {rich.table.total - rich.table.rows.length} more rows beyond the captured {rich.table.rows.length} (CSV gets everything captured)</span>
            ) : null}
          </div>
        ) : (
          <GrafanaResultView view={rich.view} traceOf={traceOf} run={run} onSelectTrace={onSelectTrace} />
        )
      ) : rawRec ? (
        <AnnotatedFileBody rec={rawRec} traceOf={traceOf} onSelectTrace={onSelectTrace} cap={resCap} bordered />
      ) : (
        <span className={NOTE_CLASS}>no payload captured</span>
      )}
    </div>
  );
}

// The whole consultation in the detail sidebar: the agent's articulation of
// its task (request + facets) and the recalled world (topics, neighbourhood,
// candidates with authors, the server's drop disclosures). `counts` is the
// WINDOW-wide recall tally ({ sessionId: consultations }) so a candidate the
// whole window keeps coming back to is flagged inside any one consultation.
// The detail host mounts content into an overflow-hidden cell — this
// component (like every rich detail) must bring its own scroll container.
export function RecallConsultationDetail({ consultation: c, labelOf, onSelectTrace, counts = {}, onOpenSession = null, bare = false }) {
  const v = c.view;
  const byTopic = new Map();
  for (const cand of v?.candidates || []) {
    const k = cand.topic || "(unrouted — semantic probe)";
    if (!byTopic.has(k)) byTopic.set(k, []);
    byTopic.get(k).push(cand);
  }
  // Biggest recall groups first — the server's row order is time-based, but a
  // reader scans groups by weight.
  const topicGroups = [...byTopic.entries()].sort((a, b) => b[1].length - a[1].length);
  const fields = [
    c.mode === "map" ? "map mode" : null,
    v?.windowDays != null ? `${v.windowDays}d window` : c.windowDays != null ? `${c.windowDays}d window` : null,
    c.maxCandidates != null ? `cap ${c.maxCandidates}` : null,
  ].filter(Boolean);
  const body = (
    <>
      <div className="min-w-0">
        <div className="flex items-baseline gap-[8px] pb-[3px]">
          <span className="text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Asked</span>
          <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{sampleWhen(c.ts)}</span>
          <span className="text-[10px] text-[color:var(--muted)]">{labelOf({ id: c.endpointId, label: c.endpointName })}</span>
          {fields.length ? <span className="text-[10px] text-[color:var(--muted-soft)]">{fields.join(" · ")}</span> : null}
          {c.promptId && onSelectTrace ? (
            <button
              type="button"
              onClick={() => onSelectTrace(c.promptId)}
              className="ml-auto shrink-0 cursor-pointer text-[10px] text-[color:var(--muted-soft)] hover:text-[color:var(--ink)]"
            >
              open prompt →
            </button>
          ) : null}
        </div>
        <div className="whitespace-pre-wrap text-[12px] leading-[1.55] text-[color:var(--ink)]">{c.request || "(no request text)"}</div>
        {c.facets.length ? (
          <div className="flex min-w-0 flex-wrap gap-[6px] pt-[6px]">
            {c.facets.map((f, i) => (
              <span key={i} className="rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-[10.5px] text-[color:var(--muted)]">{f}</span>
            ))}
          </div>
        ) : null}
      </div>
      {!c.ok ? (
        <span className={NOTE_CLASS}>The consultation failed. No result reached the agent.</span>
      ) : v?.type === "spilled" ? (
        <span className={NOTE_CLASS}>
          The result exceeded the inline limit and was saved locally. Its contents are outside this capture.
        </span>
      ) : v?.type === "none" ? (
        <span className={NOTE_CLASS}>{v.usageNote || "No topic in the tenant's recent history matched — the agent proceeded without precedent."}</span>
      ) : v ? (
        <>
          {v.topics.length ? (
            <div className="min-w-0">
              <div className="pb-[3px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Routed topics</div>
              {v.topics.map((topic, i) => (
                <div key={topic.id ?? i} className="min-w-0 py-[3px]">
                  <div className="flex min-w-0 items-baseline gap-[8px]">
                    <span className="min-w-0 truncate text-[11.5px] text-[color:var(--ink)]">{topic.name}</span>
                    {topic.points != null ? <span className="shrink-0 font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{topic.points} pts</span> : null}
                  </div>
                  {topic.description ? (
                    <div className="min-w-0 overflow-hidden text-[10.5px] leading-[1.45] text-[color:var(--muted)] [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                      {topic.description}
                    </div>
                  ) : null}
                </div>
              ))}
              {v.neighbours.length ? (
                <div className="flex min-w-0 flex-wrap items-baseline gap-[6px] pt-[4px]">
                  <span className="shrink-0 text-[10px] text-[color:var(--muted-soft)]">neighbourhood:</span>
                  {v.neighbours.map((n, i) => (
                    <span key={n.id ?? i} className="min-w-0 max-w-full truncate rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] px-[6px] py-[1px] text-[10.5px] text-[color:var(--muted)]">
                      {n.name}{n.sharedSessions != null ? <span className="font-mono tabular-nums text-[color:var(--muted-soft)]"> ·{n.sharedSessions}</span> : null}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
          {v.candidates?.length ? (
            <div className="min-w-0">
              <div className="pb-[3px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">
                Recalled candidates · {v.candidates.length}{v.candidatesTruncated ? "+" : ""}
              </div>
              {topicGroups.map(([topic, cands]) => (
                <div key={topic} className="min-w-0 pb-[4px]">
                  {/* The detail host paints var(--bg), not the panels'
                      plot-bg — the sticky patch must match its own root. */}
                  <div className="sticky top-0 z-[1] truncate bg-[color:var(--bg)] py-[2px] text-[10.5px] text-[color:var(--muted)]">
                    {topic} <span className="font-mono tabular-nums text-[color:var(--muted-soft)]">×{cands.length}</span>
                  </div>
                  {/* Full-width flowing rows: inline meta (date · author · ×N)
                      leads the summary in ONE wrapping paragraph — columns
                      wasted a third of the sidebar on gutters and sized
                      differently per group. */}
                  {cands.map((cand, i) => {
                    const inner = (
                      <>
                        <span className="font-mono text-[10px] tabular-nums text-[color:var(--muted-soft)]">{(cand.date || "").slice(0, 10)}</span>
                        <span className="text-[10px] text-[color:var(--muted)]"> {cand.author || "unknown"}</span>
                        {counts[cand.sessionId] > 1 ? (
                          <span className="font-mono text-[10px] tabular-nums" style={PLUS_STYLE}> ×{counts[cand.sessionId]}</span>
                        ) : null}
                        <span className="text-[color:var(--muted-soft)]"> — </span>
                        {cand.summary}
                      </>
                    );
                    return onOpenSession && cand.sessionId ? (
                      <button
                        key={`${cand.sessionId}:${i}`}
                        type="button"
                        onClick={() => onOpenSession(cand.sessionId)}
                        className="group/cand block w-full min-w-0 cursor-pointer py-[2px] pl-[8px] text-left text-[11px] leading-[1.5] text-[color:var(--ink)] hover:text-[color:var(--green)]"
                      >
                        {inner}
                        <span className="ml-[6px] text-[10px] text-[color:var(--muted-soft)] opacity-0 group-hover/cand:opacity-100">session →</span>
                      </button>
                    ) : (
                      <div key={`${cand.sessionId}:${i}`} className="min-w-0 py-[2px] pl-[8px] text-[11px] leading-[1.5] text-[color:var(--ink)]">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              ))}
              {v.candidatesTruncated ? (
                <span className={NOTE_CLASS}>Candidate list limited for display. The agent received the full set.</span>
              ) : null}
              {v.disclosures ? (
                <span className={`${NOTE_CLASS} block pt-[4px]`}>
                  Server returned {v.disclosures.returned ?? "?"}
                  {v.disclosures.droppedDuplicates ? ` · dropped ${v.disclosures.droppedDuplicates} duplicates` : ""}
                  {v.disclosures.droppedPerTopicCap ? ` · ${v.disclosures.droppedPerTopicCap} over the per-topic cap (${v.disclosures.perTopicCap})` : ""}
                  {v.disclosures.droppedOverGlobalCap ? ` · ${v.disclosures.droppedOverGlobalCap} over the global cap` : ""}
                </span>
              ) : null}
            </div>
          ) : v.type === "map" ? (
            <span className={NOTE_CLASS}>Map mode requested topics without candidates.</span>
          ) : null}
        </>
      ) : c.result?.text ? (
        <div className="min-w-0">
          <div className="pb-[3px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--eyebrow)]">Raw result</div>
          <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap break-words rounded-[2px] border border-[color:var(--line-soft)] bg-[color:var(--control-bg)] p-[8px] font-mono text-[10.5px] leading-[1.5] text-[color:var(--muted)]">{c.result.text}</pre>
          {c.result.truncated ? <span className={NOTE_CLASS}>truncated</span> : null}
        </div>
      ) : (
        <span className={NOTE_CLASS}>No result was captured for this consultation in the sampled bodies.</span>
      )}
    </>
  );
  // The standalone detail host mounts into an overflow-hidden cell, so it needs
  // this component's own scroll container; the trace-detail feed's panel owns
  // the only scroll surface there, so `bare` drops it.
  if (bare) return <div className="grid min-w-0 gap-[10px]">{body}</div>;
  return (
    <ScrollArea className="min-h-0 h-full" contentClassName="grid min-w-0 gap-[12px] px-[2px] py-[8px]">
      {body}
    </ScrollArea>
  );
}
